import { useMemo, useState } from "react";
import { MediaField } from "@/components/admin/MediaField";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RecipeEditor } from "@/components/admin/RecipeEditor";
import {
  listSaladsAdmin, listSaladCategoriesAdmin, upsertSalad, archiveSalad,
  setSaladSlots, upsertSaladCategory,
  type SaladAdminRow, type CategoryRow,
} from "@/lib/admin/soups-salads.functions";
import {
  SS_AVAILABILITY_STATUSES,
  type SaladInput, type SaladCategoryInput,
} from "@/lib/admin/soups-salads.schemas";

export const Route = createFileRoute("/_admin/admin/salads")({
  head: () => ({ meta: [{ title: "Salads · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminSaladsPage,
});

const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MEAL_SLOTS = ["breakfast","lunch","snacks","dinner"] as const;

function AdminSaladsPage() {
  const listFn = useServerFn(listSaladsAdmin);
  const catFn = useServerFn(listSaladCategoriesAdmin);
  const qc = useQueryClient();
  const saladsQ = useQuery({ queryKey: ["admin","salads"], queryFn: () => listFn({}) });
  const catsQ  = useQuery({ queryKey: ["admin","salad-cats"], queryFn: () => catFn({}) });
  const salads = (saladsQ.data as { items: SaladAdminRow[] } | undefined)?.items ?? [];
  const categories = (catsQ.data as { items: CategoryRow[] } | undefined)?.items ?? [];

  const [search, setSearch] = useState("");
  const [openSalad, setOpenSalad] = useState<SaladAdminRow | null | "new">(null);
  const [confirmArchive, setConfirmArchive] = useState<SaladAdminRow | null>(null);
  const [openCat, setOpenCat] = useState<CategoryRow | null | "new">(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return salads;
    return salads.filter((b) =>
      b.name.toLowerCase().includes(q) || b.slug.toLowerCase().includes(q) ||
      (b.description ?? "").toLowerCase().includes(q));
  }, [salads, search]);

  const upsertFn = useServerFn(upsertSalad);
  const archiveFn = useServerFn(archiveSalad);
  const slotsFn = useServerFn(setSaladSlots);
  const upsertCatFn = useServerFn(upsertSaladCategory);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin","salads"] });
    qc.invalidateQueries({ queryKey: ["admin","salad-cats"] });
  };
  const saveSalad = useMutation({
    mutationFn: async (v: { id?: string; input: SaladInput; slots: string[] }) => {
      const res = await upsertFn({ data: { id: v.id, input: v.input } });
      if (res && "id" in res && res.id) await slotsFn({ data: { id: res.id, slots: v.slots } });
      return res;
    },
    onSuccess: () => { toast.success("Salad saved"); setOpenSalad(null); refresh(); },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });
  const archiveM = useMutation({
    mutationFn: async (id: string) => archiveFn({ data: { id } }),
    onSuccess: () => { toast.success("Salad archived"); refresh(); },
    onError: (e: Error) => toast.error(`Archive failed: ${e.message}`),
  });
  const saveCat = useMutation({
    mutationFn: async (v: { id?: string; input: SaladCategoryInput }) => upsertCatFn({ data: v }),
    onSuccess: () => { toast.success("Category saved"); setOpenCat(null); refresh(); },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  return (
    <div>
      <AdminPageHeader
        title="Salads"
        description="Manage the salad library and categories. Shares the à-la-carte cart with food & beverages."
        actions={
          <AdminDataToolbar
            exportName="salads"
            moduleId="salads"
            onImported={refresh}
            rows={() =>
              filtered.map((s) => ({
                Slug: s.slug,
                Name: s.name,
                Description: s.description ?? "",
                Veg: s.is_veg ? "Yes" : "No",
                Active: s.is_active ? "Yes" : "No",
              }))
            }
          />
        }
      />

      <Tabs defaultValue="library" className="mt-4">
        <TabsList>
          <TabsTrigger value="library">Library</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>
        <TabsContent value="library" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input placeholder="Search salads…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
            <Button onClick={() => setOpenSalad("new")}><Plus className="mr-2 h-4 w-4" /> New salad</Button>
          </div>
          {saladsQ.isLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <Alert><AlertDescription>No salads match.</AlertDescription></Alert>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Name</th><th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Dressing</th><th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Price</th><th className="px-3 py-2">Complete</th>
                    <th className="px-3 py-2">Public</th><th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => {
                    const cat = categories.find((c) => c.id === b.category_id);
                    return (
                      <tr key={b.id} className="border-t">
                        <td className="px-3 py-2">
                          <div className="font-medium">{b.name}</div>
                          <div className="text-xs text-muted-foreground">{b.slug}</div>
                        </td>
                        <td className="px-3 py-2">{cat?.name ?? "—"}</td>
                        <td className="px-3 py-2">
                          {b.dressing_name ? `${b.dressing_name}${b.dressing_included ? "" : " (side)"}` : "—"}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={b.availability_status === "in_stock" ? "default" : "secondary"}>
                            {b.availability_status.replace(/_/g, " ")}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          {b.current_price_paise != null ? `₹${(b.current_price_paise / 100).toFixed(0)}` : <span className="text-destructive">unset</span>}
                        </td>
                        <td className="px-3 py-2 tabular-nums">{b.completeness_score ?? 0}%</td>
                        <td className="px-3 py-2">
                          {b.is_public && b.is_active ? <Badge>Live</Badge> : <Badge variant="outline">Hidden</Badge>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => setOpenSalad(b)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => setConfirmArchive(b)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setOpenCat("new")}><Plus className="mr-2 h-4 w-4" /> New category</Button>
          </div>
          <div className="rounded-md border">
            {catsQ.isLoading ? (
              <div className="p-3 text-sm text-muted-foreground">Loading…</div>
            ) : categories.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground">No categories yet.</div>
            ) : (
              <ul className="divide-y">
                {categories.map((c) => (
                  <li key={c.id} className="flex items-center justify-between p-3">
                    <div><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.slug}</div></div>
                    <div className="flex items-center gap-2">
                      {c.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Hidden</Badge>}
                      <Button size="sm" variant="ghost" onClick={() => setOpenCat(c)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {openSalad ? (
        <SaladDialog
          existing={openSalad === "new" ? null : openSalad}
          categories={categories}
          onClose={() => setOpenSalad(null)}
          onSave={(v) => saveSalad.mutate(v)}
          saving={saveSalad.isPending}
        />
      ) : null}
      {openCat ? (
        <CategoryDialog
          existing={openCat === "new" ? null : openCat}
          onClose={() => setOpenCat(null)}
          onSave={(v) => saveCat.mutate(v)}
          saving={saveCat.isPending}
        />
      ) : null}

      <AlertDialog open={confirmArchive !== null} onOpenChange={(v) => !v && setConfirmArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this salad?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes "{confirmArchive?.name}" from the public site. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmArchive) { archiveM.mutate(confirmArchive.id); setConfirmArchive(null); } }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SaladDialog({
  existing, categories, onClose, onSave, saving,
}: {
  existing: SaladAdminRow | null;
  categories: CategoryRow[];
  onClose: () => void;
  onSave: (v: { id?: string; input: SaladInput; slots: string[] }) => void;
  saving: boolean;
}) {
  const [f, setF] = useState<SaladInput>(() => ({
    slug: existing?.slug ?? "",
    name: existing?.name ?? "",
    description: existing?.description ?? null,
    long_description: existing?.long_description ?? null,
    category_id: existing?.category_id ?? null,
    salad_type: existing?.salad_type ?? null,
    image_url: existing?.image_url ?? null,
    alt_text: existing?.alt_text ?? null,
    serving_size: existing?.serving_size ?? null,
    serving_unit: existing?.serving_unit ?? null,
    is_veg: existing?.is_veg ?? true,
    is_vegan: existing?.is_vegan ?? false,
    is_jain: existing?.is_jain ?? false,
    ingredients: existing?.ingredients ?? [],
    toppings: existing?.toppings ?? [],
    dressing_name: existing?.dressing_name ?? null,
    dressing_included: existing?.dressing_included ?? true,
    allergens: existing?.allergens ?? [],
    calories_kcal: (existing?.calories_kcal as number | null) ?? null,
    protein_g: (existing?.protein_g as number | null) ?? null,
    carbs_g: (existing?.carbs_g as number | null) ?? null,
    fat_g: (existing?.fat_g as number | null) ?? null,
    fibre_g: (existing?.fibre_g as number | null) ?? null,
    sugar_g: (existing?.sugar_g as number | null) ?? null,
    sodium_mg: (existing?.sodium_mg as number | null) ?? null,
    available_days: existing?.available_days ?? [0,1,2,3,4,5,6],
    availability_status: (existing?.availability_status as SaladInput["availability_status"]) ?? "in_stock",
    sort_order: existing?.sort_order ?? 0,
    is_active: existing?.is_active ?? true,
    is_public: existing?.is_public ?? true,
    alacarte_active: existing?.alacarte_active ?? true,
    is_demo: existing?.is_demo ?? false,
    price_paise: existing?.current_price_paise ?? null,
  }));
  const [slots, setSlots] = useState<string[]>(["lunch","dinner"]);

  const toggleDay = (d: number) => setF((s) => ({
    ...s,
    available_days: s.available_days.includes(d)
      ? s.available_days.filter((x) => x !== d)
      : [...s.available_days, d].sort(),
  }));
  const toggleSlot = (s: string) => setSlots((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  const submit = (e: React.FormEvent) => { e.preventDefault(); onSave({ id: existing?.id, input: f, slots }); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[95vw] overflow-y-auto sm:max-w-[90vw] xl:max-w-[1500px]">
        <DialogHeader><DialogTitle>{existing ? "Edit salad" : "New salad"}</DialogTitle></DialogHeader>
        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="ingredients">Ingredients</TabsTrigger>
          </TabsList>
          <TabsContent value="ingredients" className="pt-4">
            <RecipeEditor kind="salad" itemId={existing?.id ?? null} itemTitle={f.name} />
          </TabsContent>
          <TabsContent value="details">
        <form onSubmit={submit} className="space-y-4">

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
            <Field label="Slug"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} required /></Field>
            <Field label="Category">
              <select className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.category_id ?? ""}
                onChange={(e) => setF({ ...f, category_id: e.target.value || null })}>
                <option value="">— None —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Price (₹)">
              <Input type="number" min="0"
                value={f.price_paise == null ? "" : String(f.price_paise / 100)}
                onChange={(e) => setF({ ...f, price_paise: e.target.value === "" ? null : Math.round(Number(e.target.value) * 100) })}
                placeholder="e.g. 220" />
            </Field>
          </div>
          <Field label="Description">
            <Textarea rows={3} value={f.description ?? ""} onChange={(e) => setF({ ...f, description: e.target.value || null })} />
          </Field>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Image"><MediaField value={f.image_url ?? ""} onChange={(next) => setF({ ...f, image_url: next || null })} /></Field>
            <Field label="Alt text"><Input value={f.alt_text ?? ""} onChange={(e) => setF({ ...f, alt_text: e.target.value || null })} /></Field>
            <Field label="Salad type"><Input value={f.salad_type ?? ""} onChange={(e) => setF({ ...f, salad_type: e.target.value || null })} placeholder="grain, garden, bowl…" /></Field>
            <Field label="Serving size"><Input value={f.serving_size ?? ""} onChange={(e) => setF({ ...f, serving_size: e.target.value || null })} placeholder="e.g. 250 g" /></Field>
            <Field label="Serving unit"><Input value={f.serving_unit ?? ""} onChange={(e) => setF({ ...f, serving_unit: e.target.value || null })} placeholder="bowl, plate…" /></Field>
            <Field label="Availability">
              <select className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={f.availability_status}
                onChange={(e) => setF({ ...f, availability_status: e.target.value as SaladInput["availability_status"] })}>
                {SS_AVAILABILITY_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </Field>
            <Field label="Dressing name"><Input value={f.dressing_name ?? ""} onChange={(e) => setF({ ...f, dressing_name: e.target.value || null })} /></Field>
            <ToggleRow label="Dressing included" checked={f.dressing_included} onChange={(v) => setF({ ...f, dressing_included: v })} />
          </div>
          <div className="grid gap-3 md:grid-cols-7">
            {(["calories_kcal","protein_g","carbs_g","fat_g","fibre_g","sugar_g","sodium_mg"] as const).map((k) => (
              <Field key={k} label={k.replace(/_/g," ")}>
                <Input type="number" min="0" step="0.1"
                  value={f[k] == null ? "" : String(f[k])}
                  onChange={(e) => setF({ ...f, [k]: e.target.value === "" ? null : Number(e.target.value) } as SaladInput)} />
              </Field>
            ))}
          </div>
          <Field label="Available days of the week">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm"
                onClick={() => setF({ ...f, available_days: [0,1,2,3,4,5,6] })}>All days</Button>
              {DAYS.map((d, i) => {
                const on = f.available_days.includes(i);
                return <Button key={i} type="button" size="sm" variant={on ? "default" : "outline"} onClick={() => toggleDay(i)}>{d}</Button>;
              })}
            </div>
          </Field>
          <Field label="Meal slots (à la carte)">
            <div className="flex flex-wrap gap-2">
              {MEAL_SLOTS.map((s) => {
                const on = slots.includes(s);
                return <Button key={s} type="button" size="sm" variant={on ? "default" : "outline"} onClick={() => toggleSlot(s)}>{s}</Button>;
              })}
            </div>
          </Field>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Ingredients (comma separated)">
              <Input value={(f.ingredients ?? []).join(", ")} onChange={(e) => setF({ ...f, ingredients: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </Field>
            <Field label="Toppings (comma separated)">
              <Input value={(f.toppings ?? []).join(", ")} onChange={(e) => setF({ ...f, toppings: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </Field>
            <Field label="Allergens (comma separated)">
              <Input value={(f.allergens ?? []).join(", ")} onChange={(e) => setF({ ...f, allergens: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </Field>
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            <ToggleRow label="Veg" checked={f.is_veg} onChange={(v) => setF({ ...f, is_veg: v })} />
            <ToggleRow label="Vegan" checked={f.is_vegan} onChange={(v) => setF({ ...f, is_vegan: v })} />
            <ToggleRow label="Jain" checked={f.is_jain} onChange={(v) => setF({ ...f, is_jain: v })} />
            <ToggleRow label="Public" checked={f.is_public} onChange={(v) => setF({ ...f, is_public: v })} />
            <ToggleRow label="Active" checked={f.is_active} onChange={(v) => setF({ ...f, is_active: v })} />
            <ToggleRow label="À la carte" checked={f.alacarte_active} onChange={(v) => setF({ ...f, alacarte_active: v })} />
            <ToggleRow label="Demo" checked={f.is_demo} onChange={(v) => setF({ ...f, is_demo: v })} />
            <Field label="Sort order">
              <Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) || 0 })} />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
          </TabsContent>
        </Tabs>

      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({
  existing, onClose, onSave, saving,
}: {
  existing: CategoryRow | null;
  onClose: () => void;
  onSave: (v: { id?: string; input: SaladCategoryInput }) => void;
  saving: boolean;
}) {
  const [f, setF] = useState<SaladCategoryInput>(() => ({
    slug: existing?.slug ?? "",
    name: existing?.name ?? "",
    description: existing?.description ?? null,
    sort_order: existing?.sort_order ?? 0,
    is_active: existing?.is_active ?? true,
    is_demo: existing?.is_demo ?? false,
  }));
  const submit = (e: React.FormEvent) => { e.preventDefault(); onSave({ id: existing?.id, input: f }); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{existing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
          <Field label="Slug"><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} required /></Field>
          <Field label="Description"><Textarea rows={2} value={f.description ?? ""} onChange={(e) => setF({ ...f, description: e.target.value || null })} /></Field>
          <Field label="Sort order"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) || 0 })} /></Field>
          <ToggleRow label="Active" checked={f.is_active} onChange={(v) => setF({ ...f, is_active: v })} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2">
      <Label className="text-sm">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
